import React, { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Sidebar from "../../../../components/Layout/Sidebar";
import Header from "../../../../components/Layout/Header";
import Footer from "../../../../components/Layout/Footer";
import FormSkeleton from "../../../components/skeleton/FormSkeleton";
import RichEditorWithFeatures from "../../../../components/presentations/RichEditorWithFeatures";

import { Form } from "react-bootstrap";
import Swal from "sweetalert2";

import {
  getData,
  getParticipantTypeData,
} from "../../../../services/event/participants/participantSetupService";

import {
  addData,
  getPresentationByParticipantName,
  updateData,
} from "../../../../services/event/presentations/presentationSetupService";

import {
  addData as addVersion,
  updateData as updateVersion,
  deleteData as deleteVersion,
} from "../../../../services/event/presentations/presentationVersionService";

import {
  addData as addComment,
  updateData as updateComment,
  deleteData as deleteComment,
} from "../../../../services/event/presentations/presentationCommentService";

import { nanoid } from "nanoid";
import { isQuillContentEmpty } from "../../../../utils/quill_helper";
import { onsite_url } from "../../../../config";
import { validateImageFile } from "../../../../utils/fileValidation";

const sub_domain = localStorage.getItem("sub_domain");
const series = localStorage.getItem("series");


/* ─── Current user from auth/localStorage ──────────────────────── */
const CURRENT_USER = {
  name: localStorage.getItem("sub_domain") || "Admin",
  role: localStorage.getItem("role")    || "admin",
};

/* ══════════════════════════════════════════════════════════════════
   versionsApi — backed by presentationVersionService
   Uses the same POST-based definePost pattern as all other controllers:
     Case 2: POST { presentation_uuid, field }        → filtered list
     Case 3: POST { id: "", ...fields }               → create snapshot
══════════════════════════════════════════════════════════════════ */
const versionsApi = {
  // Fetch all versions for a presentation + field  (Case 2)
  list: async (uuid, field) => {
    try {
      const res = await addVersion({ presentation_uuid: uuid, field });
      return Array.isArray(res?.data) ? res.data : [];
    } catch { return []; }
  },

  // Create a new version snapshot  (Case 3)
  save: async (uuid, field, content, savedBy) => {
    try {
      const res = await addVersion({
        id:                "",
        presentation_uuid: uuid,
        field,
        content,
        saved_by:          savedBy,
      });
      return res?.data || null;
    } catch { return null; }
  },
};

/* ══════════════════════════════════════════════════════════════════
   commentsApi — backed by presentationCommentService
   Uses the same POST-based definePost pattern:
     Case 2: POST { presentation_uuid, field }        → filtered list
     Case 3: POST { id: "", ...fields }               → create comment
     PUT    { id, resolved: true }                    → resolve
     DELETE { id }                                    → delete
══════════════════════════════════════════════════════════════════ */
const commentsApi = {
  // Fetch all comments for a presentation + field  (Case 2)
  list: async (uuid, field) => {
    try {
      const res = await addComment({ presentation_uuid: uuid, field });
      return Array.isArray(res?.data) ? res.data : [];
    } catch { return []; }
  },

  // Create a new comment or instruction  (Case 3)
  add: async (uuid, field, comment) => {
    try {
      const res = await addComment({
        id:                "",
        presentation_uuid: uuid,
        field,
        ...comment,        // { author, role, text, type }
      });
      return res?.data || null;
    } catch { return null; }
  },

  // Resolve a comment — PUT { id, resolved: true }
  resolve: async (id) => {
    try {
      const res = await updateComment({ id, resolved: true });
      return res?.data || null;
    } catch { return null; }
  },

  // Delete a comment — DELETE { id }
  remove: async (id) => {
    try {
      const res = await deleteComment({ id });
      return res?.data || null;
    } catch { return null; }
  },
};

/* ── Styles ─────────────────────────────────────────────────────── */
const S = {
  page: { background: "#f4f6f9", minHeight: "100vh", fontFamily: "'DM Sans', sans-serif" },
  container: { padding: "28px 28px 0" },
  topBar: {
    display: "flex", alignItems: "flex-start", justifyContent: "space-between",
    marginBottom: 24, flexWrap: "wrap", gap: 10,
  },
  breadcrumb: {
    fontSize: 12, color: "#9aa5b4", fontWeight: 600,
    letterSpacing: 1, textTransform: "uppercase", marginBottom: 3,
  },
  pageTitle: { fontSize: 22, fontWeight: 700, color: "#1a2332", margin: 0 },
  backBtn: {
    display: "inline-flex", alignItems: "center", gap: 6,
    padding: "8px 18px", border: "1.5px solid #e3e8ef", borderRadius: 8,
    background: "#fff", color: "#4a5568", fontSize: 13, fontWeight: 600,
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s",
  },
  sectionCard: {
    background: "#fff", borderRadius: 14, border: "1px solid #e3e8ef",
    marginBottom: 20, overflow: "hidden",
    boxShadow: "0 1px 8px rgba(26,107,60,0.05)",
  },
  sectionHeader: {
    padding: "14px 20px", borderBottom: "1px solid #f0f4f8",
    display: "flex", alignItems: "center", gap: 10,
  },
  sectionIcon: {
    width: 30, height: 30, borderRadius: 8, background: "#e8f5ee",
    display: "flex", alignItems: "center", justifyContent: "center",
    fontSize: 14, color: "#1a6b3c", flexShrink: 0,
  },
  sectionTitle: { fontSize: 14, fontWeight: 700, color: "#1a2332", margin: 0 },
  sectionBody: { padding: "20px 20px 12px" },
  label: {
    fontSize: 12.5, fontWeight: 700, color: "#4a5568",
    marginBottom: 6, display: "block", letterSpacing: 0.2,
  },
  requiredStar: { color: "#c0392b", marginLeft: 2 },
  controlBase: {
    fontSize: 13.5, borderRadius: 8,
    borderColor: "#e3e8ef", fontFamily: "'DM Sans', sans-serif",
  },
  uuidWrap: {
    display: "flex", alignItems: "center", border: "1.5px solid #e3e8ef",
    borderRadius: 8, overflow: "hidden", background: "#f8fafb",
  },
  uuidInput: {
    flex: 1, border: "none", background: "transparent",
    padding: "8px 12px", fontSize: 12.5,
    fontFamily: "monospace", color: "#6b7a8d", outline: "none",
  },
  uuidLabel: {
    padding: "6px 12px", background: "#e8f5ee", color: "#1a6b3c",
    fontSize: 11, fontWeight: 700, letterSpacing: 0.5,
    borderLeft: "1px solid #d0e4d8", whiteSpace: "nowrap",
  },
  fileZone: {
    border: "2px dashed #d8e4ec", borderRadius: 10, padding: "18px 16px",
    textAlign: "center", cursor: "pointer", transition: "all 0.15s",
    background: "#fafcfe", position: "relative",
  },
  fileInput: {
    position: "absolute", inset: 0, opacity: 0,
    cursor: "pointer", width: "100%", height: "100%",
  },
  btnRow: {
    display: "flex", gap: 10, paddingTop: 4,
    paddingBottom: 28, justifyContent: "flex-end",
  },
  cancelBtn: {
    padding: "10px 22px", border: "1.5px solid #e3e8ef", borderRadius: 8,
    background: "#fff", color: "#4a5568", fontSize: 13.5, fontWeight: 600,
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.15s",
  },
  submitBtn: {
    padding: "10px 28px", border: "none", borderRadius: 8,
    background: "#1a6b3c", color: "#fff", fontSize: 13.5, fontWeight: 600,
    cursor: "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.18s",
    display: "inline-flex", alignItems: "center", gap: 8,
  },
};

/* ── Helpers ─────────────────────────────────────────────────────── */
// Resolve relative file paths against onsite_url (same base used by interceptorServices)
const toFullUrl = (url) => {
  if (!url) return "";
  if (url.startsWith("http")) return url;
  const base = (onsite_url || "http://localhost:8181").replace(/\/$/, "");
  return `${base}/${url.replace(/^\//, "")}`;
};

const fmtSize = (b) =>
  !b ? "" : b < 1024 ? `${b}B` : b < 1048576 ? `${(b / 1024).toFixed(0)}KB` : `${(b / 1048576).toFixed(1)}MB`;

const fmtDate = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

/* ══════════════════════════════════════════════════════════════════
   TitleFieldWithFeatures
   Plain-text input with Version History + Comments stored in DB
   (same UX pattern as RichEditorWithFeatures)
══════════════════════════════════════════════════════════════════ */
function TitleFieldWithFeatures({
  field, label, value, onChange, error, required,
  currentUser, presentationId, placeholder,type
}) {
  const [panel, setPanel]       = useState(null); // null | 'versions' | 'comments'
  const [versions, setVersions] = useState([]);
  const [comments, setComments] = useState([]);
  const [vLoading, setVLoading] = useState(false);
  const [cLoading, setCLoading] = useState(false);
  const [newComment, setNewComment] = useState("");
  const [commentType, setCommentType] = useState("comment");
  const [diffIdx, setDiffIdx]   = useState(null);


  const isAdmin       = ["admin", "management","onsite"].includes(currentUser?.role);
  const isPresenter   = currentUser?.role === "event";

  const loadVersions = async () => {
    if (!presentationId || presentationId === "new") return;
    setVLoading(true);
    const data = await versionsApi.list(presentationId, field);
    setVersions(Array.isArray(data) ? data : []);
    setVLoading(false);
  };

  const loadComments = async () => {
    if (!presentationId || presentationId === "new") return;
    setCLoading(true);
    const data = await commentsApi.list(presentationId, field);
    setComments(Array.isArray(data) ? data : []);
    setCLoading(false);
  };
  useEffect(() => {
  if (type === "edit") {
    (async () => {
       await loadVersions();
       await loadComments();
    })();
  }
}, [type]);

  const openPanel = (p) => {
    if (panel === p) { setPanel(null); return; }
    setPanel(p);
    if (p === "versions") loadVersions();
    if (p === "comments") loadComments();
  };

  const saveVersion = async () => {
    if (!value?.trim()) return;
    if (!presentationId || presentationId === "new") {
      Swal.fire({ icon: "warning", title: "Save the presentation first", timer: 2200, showConfirmButton: false });
      return;
    }
    await versionsApi.save(presentationId, field, value, currentUser?.name);
    await loadVersions();
    Swal.fire({ icon: "success", title: "Version saved", timer: 1200, showConfirmButton: false });
  };

  const restoreVersion = (v) => {
    onChange({ target: { name: field, value: v.content } });
    setPanel(null);
    Swal.fire({ icon: "info", title: "Restored", text: "Title restored. Remember to save the form.", timer: 1800, showConfirmButton: false });
  };

  const postComment = async () => {
    if (!newComment.trim()) return;
    if (!presentationId || presentationId === "new") {
      Swal.fire({ icon: "warning", title: "Save the presentation first", timer: 2200, showConfirmButton: false });
      return;
    }
    await commentsApi.add(presentationId, field, {
      author: currentUser?.name,
      role:   currentUser?.role,
      text:   newComment.trim(),
      type:   isPresenter ? "comment" : commentType,
    });
    setNewComment("");
    await loadComments();
  };

  const resolveComment = async (id) => { await commentsApi.resolve(id); await loadComments(); };
  const deleteComment  = async (id) => { await commentsApi.remove(id);  await loadComments(); };

  const unresolvedCount   = comments.filter((c) => !c.resolved).length;
  const instructionCount  = comments.filter((c) => c.type === "instruction" && !c.resolved).length;

  return (
    <div>
      {/* Label + toolbar */}
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 6 }}>
        <label style={S.label}>
          {label}{required && <span style={S.requiredStar}>*</span>}
          {instructionCount > 0 && (
            <span style={{ marginLeft: 8, background: "#fff3cd", color: "#856404", fontSize: 11, fontWeight: 700, padding: "1px 7px", borderRadius: 20, border: "1px solid #ffd97d" }}>
              ⚠ {instructionCount} instruction{instructionCount > 1 ? "s" : ""}
            </span>
          )}
        </label>
        <div style={{ display: "flex", gap: 6 }}>
          <button type="button" onClick={saveVersion}
            style={{ padding: "4px 10px", fontSize: 11.5, fontWeight: 600, border: "1.5px solid #fff", borderRadius: 6, background: "#1a6b3c", color: "#fff", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            💾 Save Version
          </button>
          <button type="button" onClick={() => openPanel("versions")}
            style={{ padding: "4px 10px", fontSize: 11.5, fontWeight: 600, border: "1.5px solid #e3e8ef", borderRadius: 6, background: panel === "versions" ? "#f0f4f8" : "#fff", color: "#4a5568", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            🕐 History
            {versions.length > 0 && <span style={{ background: "#e3e8ef", borderRadius: 10, padding: "0 5px", fontSize: 10 }}>{versions.length}</span>}
          </button>
          <button type="button" onClick={() => openPanel("comments")}
            style={{ padding: "4px 10px", fontSize: 11.5, fontWeight: 600, border: `1.5px solid ${unresolvedCount > 0 ? "#ffd97d" : "#e3e8ef"}`, borderRadius: 6, background: panel === "comments" ? "#fff8ec" : "#fff", color: unresolvedCount > 0 ? "#856404" : "#4a5568", cursor: "pointer", display: "flex", alignItems: "center", gap: 4 }}>
            💬 Comments
            {unresolvedCount > 0 && <span style={{ background: "#ffd97d", borderRadius: 10, padding: "0 5px", fontSize: 10, color: "#856404" }}>{unresolvedCount}</span>}
          </button>
        </div>
      </div>

      {/* Text input */}
      <Form.Control
        type="text" name={field} value={value} onChange={onChange}
        isInvalid={!!error} placeholder={placeholder}
        style={{ ...S.controlBase, borderColor: error ? "#c0392b" : "#e3e8ef" }}
      />
      {error && <div style={{ fontSize: 12, color: "#c0392b", marginTop: 4 }}>{error}</div>}

      {/* Panel */}
      {panel && (
        <div style={{ marginTop: 10, border: "1.5px solid #e3e8ef", borderRadius: 10, background: "#fff", boxShadow: "0 4px 16px rgba(0,0,0,0.07)", overflow: "hidden" }}>

          {/* Panel header */}
          <div style={{ padding: "10px 16px", borderBottom: "1px solid #f0f4f8", display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafb" }}>
            <span style={{ fontWeight: 700, fontSize: 13, color: "#1a2332" }}>
              {panel === "versions" ? "🕐 Version History — Presentation Title" : "💬 Comments — Presentation Title"}
            </span>
            <button type="button" onClick={() => setPanel(null)}
              style={{ background: "none", border: "none", fontSize: 18, color: "#9aa5b4", cursor: "pointer", lineHeight: 1 }}>×</button>
          </div>

          {/* ── Versions panel ── */}
          {panel === "versions" && (
            <div style={{ padding: 14, maxHeight: 340, overflowY: "auto" }}>
              {vLoading ? (
                <div style={{ textAlign: "center", color: "#9aa5b4", padding: 24 }}>Loading…</div>
              ) : versions.length === 0 ? (
                <div style={{ textAlign: "center", color: "#9aa5b4", padding: 24, fontSize: 13 }}>
                  No versions saved yet. Click "💾 Save Version" above to create a snapshot.
                </div>
              ) : (
                [...versions].reverse().map((v, i) => (
                  <div key={v.id || i} style={{ borderBottom: "1px solid #f0f4f8", paddingBottom: 10, marginBottom: 10 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 8 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#1a2332" }}>
                          v{versions.length - i} — {v.saved_by || "Unknown"}
                        </div>
                        <div style={{ fontSize: 11, color: "#9aa5b4", marginTop: 1 }}>{fmtDate(v.saved_at || v.createdAt)}</div>
                        <div style={{ fontSize: 12.5, color: "#4a5568", marginTop: 5, background: "#f8fafb", borderRadius: 6, padding: "5px 10px", border: "1px solid #e3e8ef", wordBreak: "break-word" }}>
                          {v.content}
                        </div>
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 5, flexShrink: 0 }}>
                        <button type="button" onClick={() => setDiffIdx(diffIdx === i ? null : i)}
                          style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #e3e8ef", borderRadius: 5, background: "#fff", cursor: "pointer", color: "#4a5568" }}>
                          {diffIdx === i ? "Hide" : "Diff"}
                        </button>
                        {isAdmin && (
                          <button type="button" onClick={() => restoreVersion(v)}
                            style={{ fontSize: 11, padding: "3px 8px", border: "1px solid #1a6b3c", borderRadius: 5, background: "#e8f5ee", cursor: "pointer", color: "#1a6b3c", fontWeight: 600 }}>
                            Restore
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Diff view */}
                    {diffIdx === i && (
                      <div style={{ marginTop: 8, fontSize: 12, background: "#fffbe6", border: "1px solid #ffd97d", borderRadius: 6, padding: 8 }}>
                        <div style={{ color: "#856404", fontWeight: 700, marginBottom: 4 }}>Current vs this version:</div>
                        <div style={{ color: "#c0392b", marginBottom: 3 }}>− {value || "(empty)"}</div>
                        <div style={{ color: "#1a6b3c" }}>+ {v.content}</div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* ── Comments panel ── */}
          {panel === "comments" && (
            <div style={{ padding: 14 }}>
              {/* Thread */}
              <div style={{ maxHeight: 260, overflowY: "auto", marginBottom: 12 }}>
                {cLoading ? (
                  <div style={{ textAlign: "center", color: "#9aa5b4", padding: 20 }}>Loading…</div>
                ) : comments.length === 0 ? (
                  <div style={{ textAlign: "center", color: "#9aa5b4", padding: 20, fontSize: 13 }}>No comments yet.</div>
                ) : (
                  comments.map((c, i) => (
                    <div key={c.id || i} style={{
                      marginBottom: 10, borderRadius: 8, padding: "8px 12px",
                      background: c.type === "instruction" ? "#fffbe6" : "#f8fafb",
                      border: `1.5px solid ${c.resolved ? "#e3e8ef" : c.type === "instruction" ? "#ffd97d" : "#e3e8ef"}`,
                      opacity: c.resolved ? 0.6 : 1,
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          <span style={{ fontSize: 11, fontWeight: 700, color: "#1a2332" }}>{c.author}</span>
                          <span style={{ fontSize: 10, color: "#9aa5b4", background: "#e3e8ef", borderRadius: 4, padding: "0 5px" }}>{c.role}</span>
                      <div style={{ fontSize: 10.5, color: "#9aa5b4", marginTop: 4 }}>{fmtDate(c.created_at || c.createdAt)}</div>

                          {c.type === "instruction" && (
                            <span style={{ fontSize: 10, background: "#ffd97d", color: "#856404", borderRadius: 4, padding: "0 5px", fontWeight: 700 }}>⚠ Instruction</span>
                          )}
                          {c.resolved && (
                            <span style={{ fontSize: 10, background: "#d4edda", color: "#155724", borderRadius: 4, padding: "0 5px" }}>✓ Resolved</span>
                          )}
                        </div>
                        <div style={{ display: "flex", gap: 4 }}>
                          {isAdmin && !c.resolved && (
                            <button type="button" onClick={() => resolveComment(c.id)}
                              style={{ fontSize: 10, padding: "2px 7px", border: "1px solid #1a6b3c", borderRadius: 4, background: "#e8f5ee", color: "#1a6b3c", cursor: "pointer" }}>
                              Resolve
                            </button>
                          )}
                          {isAdmin && (
                            <button type="button" onClick={() => deleteComment(c.id)}
                              style={{ fontSize: 10, padding: "2px 7px", border: "1px solid #e3e8ef", borderRadius: 4, background: "#fff", color: "#c0392b", cursor: "pointer" }}>
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                      <div style={{ fontSize: 13, color: "#2d3748", lineHeight: 1.5 }}>{c.text}</div>
                      {/* <div style={{ fontSize: 10.5, color: "#9aa5b4", marginTop: 4 }}>{fmtDate(c.created_at || c.createdAt)}</div> */}
                    </div>
                  ))
                )}
              </div>

              {/* New comment composer */}
              <div style={{ borderTop: "1px solid #f0f4f8", paddingTop: 10 }}>
                {isAdmin && (
                  <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
                    {["comment", "instruction"].map((t) => (
                      <button key={t} type="button" onClick={() => setCommentType(t)}
                        style={{
                          fontSize: 11, padding: "3px 10px",
                          border: `1.5px solid ${commentType === t ? "#1a6b3c" : "#e3e8ef"}`,
                          borderRadius: 5,
                          background: commentType === t ? "#e8f5ee" : "#fff",
                          color: commentType === t ? "#1a6b3c" : "#4a5568",
                          cursor: "pointer", fontWeight: commentType === t ? 700 : 500,
                        }}>
                        {t === "instruction" ? "⚠ Instruction" : "💬 Comment"}
                      </button>
                    ))}
                  </div>
                )}
                <div style={{ display: "flex", gap: 8 }}>
                  <input
                    type="text" value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && postComment()}
                    placeholder={isPresenter ? "Reply to instruction…" : "Add a comment or instruction…"}
                    style={{ flex: 1, border: "1.5px solid #e3e8ef", borderRadius: 7, padding: "7px 10px", fontSize: 13, fontFamily: "'DM Sans', sans-serif", outline: "none" }}
                  />
                  <button type="button" onClick={postComment}
                    style={{ padding: "7px 14px", border: "none", borderRadius: 7, background: "#1a6b3c", color: "#fff", fontWeight: 600, fontSize: 13, cursor: "pointer" }}>
                    Post
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   FileUploadZone
   value:
     null                       → nothing selected (add mode)
     [{url,originalName,...}]   → stored files from DB (edit mode)
     File                       → new single file picked
     File[]                     → new multiple files picked
══════════════════════════════════════════════════════════════════ */
function FileUploadZone({ name, label, accept, onChange, value, onRemoveStored }) {
  const [hovered,  setHovered]  = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const storedFiles = Array.isArray(value)
    ? value.filter((v) => v && !(v instanceof File))
    : [];
  const newFiles = value instanceof File
    ? [value]
    : Array.isArray(value) ? value.filter((v) => v instanceof File) : [];
  const hasAny = storedFiles.length > 0 || newFiles.length > 0;

  /* ── Drag & drop ── */
  const handleDragOver  = (e) => { e.preventDefault(); setDragOver(true); };
  const handleDragLeave = ()  => setDragOver(false);
  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (!files.length) return;
    const dt = new DataTransfer();
    files.forEach((f) => dt.items.add(f));
    onChange({ target: { name, files: dt.files } });
  };

  return (
    <div>
      {/* Drop zone */}
      <div
        style={{
          ...S.fileZone,
          borderColor: dragOver ? "#1a6b3c" : hovered ? "#1a6b3c" : hasAny ? "#a8d5b8" : "#d8e4ec",
          background:  dragOver ? "#e8f5ee" : hovered ? "#f0fbf5" : hasAny ? "#f5fbf7" : "#fafcfe",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <input type="file" name={name} accept={accept} onChange={onChange} style={S.fileInput} multiple />
        <span style={{ fontSize: 22, display: "block", marginBottom: 6 }}>
          {dragOver ? "📂" : hasAny ? "✅" : "📎"}
        </span>
        <span style={{ fontSize: 13, color: "#6b7a8d", fontWeight: 500, display: "block", marginBottom: 2 }}>
          {dragOver ? "Drop files here" : hasAny ? "File attached — click or drag to add / replace" : label}
        </span>
        <span style={{ fontSize: 11.5, color: "#b0bec8" }}>PNG, JPG — max 10 MB • Drag &amp; drop supported</span>
      </div>

      {/* ── Existing stored files ── */}
      {storedFiles.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#1a6b3c", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
            Existing {storedFiles.length > 1 ? `${storedFiles.length} files` : "file"}
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {storedFiles.map((f, i) => {
              // ✅ Use toFullUrl so relative DB paths get onsite_url prefix
              const url   = toFullUrl(f.url);
              const isImg = f.mimetype?.startsWith("image/");
              const isPdf = f.mimetype === "application/pdf";
              return (
                <div key={i} style={{ position: "relative", width: 110, borderRadius: 8, border: "1.5px solid #e3e8ef", overflow: "hidden", background: "#fff" }}>
                  <div style={{ width: 110, height: 80, background: "#f4f6f9", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>

                    {isImg ? (
                      <img
                        src={url} 
                        alt={f.originalName}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => {
                          // Show fallback icon if image fails to load
                          e.target.style.display = "none";
                          if (e.target.nextElementSibling) {
                            e.target.nextElementSibling.style.display = "flex";
                          }
                        }}
                      />
                    ) : null}

                    {/* Fallback / non-image icons */}
                    <div style={{ display: isImg ? "none" : "flex", flexDirection: "column", alignItems: "center", color: isPdf ? "#c0392b" : "#2563eb" }}>
                      {isPdf ? (
                        <>
                          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0121 9.414V19a2 2 0 01-2 2z" />
                          </svg>
                          <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>PDF</span>
                        </>
                      ) : isImg ? (
                        // Image fallback placeholder (shown via onError)
                        <>
                          <span style={{ fontSize: 24 }}>🖼️</span>
                          <span style={{ fontSize: 9, color: "#9aa5b4", marginTop: 2 }}>No preview</span>
                        </>
                      ) : (
                        <>
                          <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                          </svg>
                          <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>DOC</span>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Name */}
                  <div style={{ padding: "4px 6px", fontSize: 10, color: "#4a5568", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", borderTop: "1px solid #f0f4f8" }} title={f.originalName}>
                    {f.originalName || f.filename || "File"}
                    {f.size && <div style={{ fontSize: 9, color: "#9aa5b4" }}>{fmtSize(f.size)}</div>}
                  </div>

                  {/* View */}
                  <a href={url} target="_blank" rel="noreferrer"
                    style={{ display: "block", textAlign: "center", padding: "3px 6px", fontSize: 10.5, fontWeight: 600, color: "#1a6b3c", background: "#f5fbf7", textDecoration: "none", borderTop: "1px solid #f0f4f8" }}>
                    View ↗
                  </a>

                  {/* Remove */}
                  {onRemoveStored && (
                    <button type="button" onClick={() => onRemoveStored(name, i)} title="Remove"
                      style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(192,57,43,0.85)", color: "#fff", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 700, lineHeight: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      ×
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Newly selected files ── */}
      {newFiles.length > 0 && (
        <div style={{ marginTop: 10 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "#2563eb", letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 6 }}>
            New — will upload on save
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
            {newFiles.map((file, i) => {
              const isImg   = file.type?.startsWith("image/");
              const isPdf   = file.type === "application/pdf";
              const blobUrl = URL.createObjectURL(file);
              return (
                <div key={i} style={{ position: "relative", width: 110, borderRadius: 8, border: "1.5px solid #c8dcf8", overflow: "hidden", background: "#fff" }}>
                  <div style={{ position: "absolute", top: 4, left: 4, background: "#1a6b3c", color: "#fff", fontSize: 8, fontWeight: 700, padding: "1px 5px", borderRadius: 4, letterSpacing: 0.5, textTransform: "uppercase" }}>New</div>
                  <div style={{ width: 110, height: 80, background: "#eff5ff", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {isImg ? (
                      <img src={blobUrl} alt={file.name} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : isPdf ? (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", color: "#c0392b" }}>
                        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414A1 1 0 0121 9.414V19a2 2 0 01-2 2z" />
                        </svg>
                        <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>PDF</span>
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", color: "#2563eb" }}>
                        <svg width="28" height="28" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                        </svg>
                        <span style={{ fontSize: 9, fontWeight: 700, marginTop: 2 }}>DOC</span>
                      </div>
                    )}
                  </div>
                  <div style={{ padding: "4px 6px", fontSize: 10, color: "#4a5568", fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", borderTop: "1px solid #f0f4f8" }} title={file.name}>
                    {file.name}
                    <div style={{ fontSize: 9, color: "#9aa5b4" }}>{fmtSize(file.size)}</div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* ── Layout helpers ──────────────────────────────────────────────── */
function Section({ icon, title, children }) {
  return (
    <div style={S.sectionCard}>
      <div style={S.sectionHeader}>
        <div style={S.sectionIcon}>{icon}</div>
        <h3 style={S.sectionTitle}>{title}</h3>
      </div>
      <div style={S.sectionBody}>{children}</div>
    </div>
  );
}

function Field({ label, required, error, children, col = "col-md-4" }) {
  return (
    <div className={`${col} mb-3`}>
      <label style={S.label}>
        {label}{required && <span style={S.requiredStar}>*</span>}
      </label>
      {children}
      {error && <div style={{ fontSize: 12, color: "#c0392b", marginTop: 4 }}>{error}</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════
   MAIN FORM
══════════════════════════════════════════════════════════════════ */
function PresentationSetupForm() {
  const navigate  = useNavigate();
  const location  = useLocation();
  const editData  = location.state?.data;
  const [type, setType] = useState(location.state?.type || "add");
  const returnUrl = "/PresentationSetupList";

  const [loading,    setLoading]    = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [errors,     setErrors]     = useState({});
  const [participantTypes, setParticipantTypes] = useState([]);
  const [dropdownData, setDropdownData] = useState({ participantSetupData: [] });

  const [form, setForm] = useState({
    id: "", uuid: "", day: "", category: "",
    participant_name: "", presentation_title: "",
    abstract: "", abstract_file: null,
    biography: "", biography_file: null,
    sub_domain:sub_domain,
    series:series ,
    remarks: "",
  });
  console.log('form',form);

  /* ── fetches ── */
  const fetchParticipantSetup = useCallback(async () => {
    try {
      const responseData = await getData();
      setDropdownData((p) => ({ ...p, participantSetupData: responseData }));
    } catch (e) { console.error(e); }
  }, []);

  const fetchParticipantTypes = useCallback(async () => {
    try {
      const res = await getParticipantTypeData();
      const filtered = res.data
        .filter((i) => i.reference_uuid === "1")
        .sort((a, b) => a.reference_uuid.localeCompare(b.reference_uuid));
      setParticipantTypes(filtered);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      await Promise.all([fetchParticipantSetup(), fetchParticipantTypes()]);
      setLoading(false);
    };
    init();
  }, [fetchParticipantSetup, fetchParticipantTypes]);

  useEffect(() => {
    if (type === "add") setForm((p) => ({ ...p, uuid: nanoid() }));
  }, [type]);

  // ✅ FIX: safely parse DB JSON string → [{url,originalName,...}]
  const parseStoredFiles = (value) => {
    if (!value) return null;
    try {
      const parsed = typeof value === "string" ? JSON.parse(value) : value;
      const arr    = Array.isArray(parsed) ? parsed : [parsed];
      return arr.length > 0 ? arr : null;
    } catch {
      return null;
    }
  };

  /* ── Select theme override ── */
const customSelectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base,
    minHeight: 38,
    fontSize: 13.5,
    borderColor: hasError ? "#c0392b" : state.isFocused ? "#1a6b3c" : "#e3e8ef",
    boxShadow: state.isFocused
      ? hasError
        ? "0 0 0 3px rgba(192,57,43,0.12)"
        : "0 0 0 3px rgba(26,107,60,0.12)"
      : "none",
    borderRadius: 8,
    fontFamily: "'DM Sans', sans-serif",
    "&:hover": { borderColor: hasError ? "#c0392b" : "#1a6b3c" },
  }),
  option: (base, state) => ({
    ...base,
    fontSize: 13.5,
    fontFamily: "'DM Sans', sans-serif",
    background: state.isSelected
      ? "#1a6b3c"
      : state.isFocused
      ? "#e8f5ee"
      : "#fff",
    color: state.isSelected ? "#fff" : "#1a2332",
  }),
  singleValue: (base) => ({ ...base, color: "#1a2332" }),
  placeholder: (base) => ({ ...base, color: "#9aa5b4", fontSize: 13.5 }),
  menu: (base) => ({ ...base, borderRadius: 8, overflow: "hidden" }),
  /* ✅ ensure the portal-rendered menu sits above everything */
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
});

  useEffect(() => {
    if (type === "edit" && editData) {
      setForm({
        id:                 editData.id,
        uuid:               editData.uuid               || "",
        day:                editData.day                || "",
        category:           editData.category           || "",
        participant_name:   editData.participant_name   || "",
        presentation_title: editData.presentation_title || "",
        abstract:           editData.abstract           || "",
        biography:          editData.biography          || "",
        remarks:            editData.remarks            || "",
        payment_receipt_id: editData.payment_receipt_id || "",
        no_of_entry:        editData.no_of_entry        || "",
        name:               editData.name               || "",
        affiliation:        editData.affiliation        || "",
        country:            editData.country            || "",
        abstract_file:  parseStoredFiles(editData.abstract_file),
        biography_file: parseStoredFiles(editData.biography_file),
      });
    }
  }, [type, editData]);

  /* ── derived options ── */
  const filteredParticipantSetupedData = dropdownData?.participantSetupData?.filter((item) => {
    const types = item.participant_type;
    if (!types) return false;
    return Object.values(types).some((cat) => cat?.[form.category] > 0);
  });

  const participantNamesOptions = filteredParticipantSetupedData?.map((item) => ({
    value: item?.payment_receipt_id,
    label: item?.name,
  }));

  const filteredParticipantTypes = participantTypes?.filter((item) => {
    if (form.day === "1") return !["E-POSTER", "VIDEO"].includes(item.category_code);
    return true;
  });

  const presentationsCategoryOptions = filteredParticipantTypes?.map((item) => ({
    value: item.category_code,
    label: item.category_name,
  }));

  const checkPresentationExist = async (participantName,day,category) => {
  const res = await getPresentationByParticipantName({participant_name:participantName,day:day,category:category});
  console.log('res',res)
  return res;
};
const handleChange = async (e) => {
  const { name, value} = e.target;
  setForm((p) => ({
    ...p,
    [name]: value,
    ...(name === "day" && { category: "" }),
  }));

  setErrors((p) => ({ ...p, [name]: "" }));

  // ✅ When participant selected → check existing record
  if (name === "participant_name" && value) {
    try {
      const res = await checkPresentationExist(value,form.day,form.category); // API call
      // const res = true; // API call

      if (res.status === 200 && res.data.length > 0) {
        // 🔥 Show popup
        Swal.fire({
          title: "Already Exists",
          text: "This participant already has a presentation. Do you want to edit it?",
          icon: "info",
          showCancelButton: true,
          confirmButtonText: "Yes, Edit",
        }).then((result) => {
          if (result.isConfirmed) {
            // ✅ Fill form with existing data
            setForm({...form, ...res.data[0]});

            // ✅ Switch to edit mode
            setType("edit");
          }
        });
      } else {
        setType("add");
      }
    } catch (err) {
      console.error(err);
    }
  }
};

  const handleRichChange = (field, value) => {
    setErrors((p) => ({
      ...p,
      [field]: isQuillContentEmpty(value) ? `${field.replace("_", " ")} is required` : "",
    }));
    setForm((p) => ({ ...p, [field]: value }));
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    if (!files || files.length === 0) return;
    // Single file → File; multiple files → File[]
    const value = files.length > 1 ? Array.from(files) : files[0];
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleRemoveStored = (fieldName, index) => {
    setForm((prev) => {
      const current = Array.isArray(prev[fieldName]) ? prev[fieldName] : [];
      const updated  = current.filter((_, i) => i !== index);
      return { ...prev, [fieldName]: updated.length > 0 ? updated : null };
    });
  };

  /* ── validation ── */
  const validateForm = () => {
    const e = {};
    if (!form.day)                e.day                = "Day is required";
    if (!form.category)           e.category           = "Category is required";
    if (!form.participant_name)   e.participant_name   = "Participant name is required";
    if (!form.presentation_title) e.presentation_title = "Presentation title is required";
    if (!form.abstract)           e.abstract           = "Abstract is required";
    if (!form.biography)          e.biography          = "Biography is required";
    if (type === "edit") {
      if (!form.remarks)
        e.remarks = "Remarks is required";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };


  const handleSubmit = async () => {
  if (!validateForm()) return;
  setBtnLoading(true);

  try {
    const formData = new FormData();

    // ✅ append normal fields
    Object.keys(form).forEach((key) => {
      if (key !== "abstract_file" && key !== "biography_file") {
        formData.append(key, form[key] ?? "");
      }
    });

    // ✅ abstract_file handling
    if (form.abstract_file instanceof File) {
      formData.append("abstract_file", form.abstract_file);
    } else if (Array.isArray(form.abstract_file)) {
      form.abstract_file.forEach((file) => {
        if (file instanceof File) {
          formData.append("abstract_file", file);
        }
      });
    }

    // ✅ biography_file handling
    if (form.biography_file instanceof File) {
      formData.append("biography_file", form.biography_file);
    } else if (Array.isArray(form.biography_file)) {
      form.biography_file.forEach((file) => {
        if (file instanceof File) {
          formData.append("biography_file", file);
        }
      });
    }
  
   
    if (type === "add") await addData(formData);
    else await updateData(formData);

    Swal.fire({
      icon: "success",
      title: "Success",
      text: `Presentation Setup ${type === "add" ? "added" : "updated"} successfully`,
      timer: 2000,
      showConfirmButton: false,
    });

    navigate(returnUrl);
  } catch (err) {
    console.error(err);
    Swal.fire({ icon: "error", title: "Error", text: "Something went wrong" });
  } finally {
    setBtnLoading(false);
  }
};

  /* ── render ── */
  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100" style={S.page}>
        <Header />
        {loading ? (
          <FormSkeleton />
        ) : (
          <div style={S.container}>

            {/* ── Top bar ── */}
            <div style={S.topBar}>
              <div>
                <div style={S.breadcrumb}>Confernece Program › Presentation Setup</div>
                <h2 style={S.pageTitle}>{type === "add" ? "Add Presentation" : "Edit Presentation"}</h2>
              </div>
              <button
                style={S.backBtn}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#1a6b3c"; e.currentTarget.style.color = "#1a6b3c"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e3e8ef"; e.currentTarget.style.color = "#4a5568"; }}
                onClick={() => navigate(returnUrl)}
              >
                ← Back to List
              </button>
            </div>

            {/* ── Section 1: Programme Details ── */}
            <Section icon="📋" title="Programme Details">
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label style={S.label}>UUID</label>
                  <div style={S.uuidWrap}>
                    <input style={S.uuidInput} value={form.uuid} readOnly />
                    <span style={S.uuidLabel}>AUTO</span>
                  </div>
                </div>

                <Field label="Day" required error={errors.day}>
                  <Form.Select name="day" value={form.day} onChange={handleChange} isInvalid={!!errors.day}
                    style={{ ...S.controlBase, borderColor: errors.day ? "#c0392b" : "#e3e8ef" }}>
                    <option value="">Select day…</option>
                    {[{ label: "Day 1", value: 1 }, { label: "Day 2", value: 2 }].map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </Form.Select>
                </Field>

                <Field label="Category" required error={errors.category}>
                  <Form.Select name="category" value={form.category} onChange={handleChange}
                    isInvalid={!!errors.category} disabled={!form.day}
                    style={{ ...S.controlBase, borderColor: errors.category ? "#c0392b" : "#e3e8ef", opacity: !form.day ? 0.55 : 1 }}>
                    <option value="">{form.day ? "Select category…" : "Select day first"}</option>
                    {presentationsCategoryOptions?.map((item, index) => (
                      <option key={`${item.value}-${index}`} value={item.value}>
                        {item.label}
                      </option>
                    ))}
                  </Form.Select>
                </Field>

                <Field label="Participant Name" required error={errors.participant_name}>
                  <Form.Select name="participant_name" value={form.participant_name} onChange={handleChange}
                    isInvalid={!!errors.participant_name} disabled={!form.category}
                    style={{ ...S.controlBase, borderColor: errors.participant_name ? "#c0392b" : "#e3e8ef", opacity: !form.category ? 0.55 : 1 }}>
                    <option value="">{form.category ? "Select participant…" : "Select category first"}</option>
                    {participantNamesOptions?.map((item,index) => (
                      <option key={`${item.value}-${index}`} value={item.value}>{item.label}</option>
                    ))}
                  </Form.Select>
                </Field>
                

                {/* ── Presentation Title with Version History + Comments (DB-backed) ── */}
                <div className="col-md-12 mb-3">
                  <TitleFieldWithFeatures
                    field="presentation_title"
                    label="Presentation Title"
                    value={form.presentation_title}
                    onChange={handleChange}
                    error={errors.presentation_title}
                    required
                    placeholder="Full title of the presentation…"
                    currentUser={CURRENT_USER}
                    presentationId={form.uuid || "new"}
                    type={type}
                  />
                </div>
              </div>
            </Section>

            {/* ── Section 2: Abstract ── */}
            <Section icon="📄" title="Abstract">
              <RichEditorWithFeatures
                field="abstract"
                label="Abstract Content"
                value={form.abstract}
                onChange={(v) => handleRichChange("abstract", v)}
                error={errors.abstract}
                currentUser={CURRENT_USER}
                presentationId={form.uuid || "new"}
                required
                // Pass DB API so RichEditorWithFeatures uses DB instead of localStorage
                versionsApi={versionsApi}
                commentsApi={commentsApi}
                type={type}
              />
              <div style={{ marginTop: 20 }}>
                <label style={{ ...S.label, marginBottom: 8 }}>
                  Supporting File <span style={{ fontSize: 11, color: "#9aa5b4", fontWeight: 500, marginLeft: 6 }}>optional</span>
                </label>
                <FileUploadZone
                  name="abstract_file"
                  label="Drag & drop or click to upload abstract file"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  value={form.abstract_file}
                  onRemoveStored={handleRemoveStored}
                />
              </div>
            </Section>

            {/* ── Section 3: Biography ── */}
            <Section icon="👤" title="Speaker Biography">
              <RichEditorWithFeatures
                field="biography"
                label="Biography Content"
                value={form.biography}
                onChange={(v) => handleRichChange("biography", v)}
                error={errors.biography}
                currentUser={CURRENT_USER}
                presentationId={form.uuid || "new"}
                required
                versionsApi={versionsApi}
                commentsApi={commentsApi}
                type={type}
              />
              <div style={{ marginTop: 20 }}>
                <label style={{ ...S.label, marginBottom: 8 }}>
                  Speaker Photo / File <span style={{ fontSize: 11, color: "#9aa5b4", fontWeight: 500, marginLeft: 6 }}>optional</span>
                </label>
                <FileUploadZone
                  name="biography_file"
                  label="Drag & drop or click to upload speaker photo or bio file"
                  accept=".png,.jpg,.jpeg"
                  onChange={handleFileChange}
                  value={form.biography_file}
                  onRemoveStored={handleRemoveStored}
                />
              </div>
            </Section>

                      <Section icon="📝" title="Additional Notes">
                          <div className="row">
                            <Field label="Remarks" col="col-md-12"  required={type === "edit"}  error={errors.remarks}>
                              <Form.Control
                                as="textarea"
                                rows={4}
                                name="remarks"
                                value={form.remarks}
                                onChange={handleChange}
                                placeholder="Any additional notes or remarks…"
                                style={{
                                  fontSize: 13.5,
                                  borderRadius: 8,
                                  borderColor: errors.remarks ? "#c0392b" : "#e3e8ef",
                                  resize: "vertical",
                                  lineHeight: 1.6,
                                }}
                                styles={customSelectStyles(!!errors.remarks)}
                              />
                            </Field>
                          </div>
                        </Section>
            

            {/* ── Submit row ── */}
            <div style={S.btnRow}>
              <button type="button" style={S.cancelBtn}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = "#c0392b"; e.currentTarget.style.color = "#c0392b"; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = "#e3e8ef"; e.currentTarget.style.color = "#4a5568"; }}
                onClick={() => navigate(returnUrl)}>
                Cancel
              </button>
              <button type="button"
                style={{ ...S.submitBtn, opacity: btnLoading ? 0.75 : 1, cursor: btnLoading ? "not-allowed" : "pointer" }}
                onMouseEnter={(e) => {
                  if (!btnLoading) {
                    e.currentTarget.style.background = "#2d8a55";
                    e.currentTarget.style.transform  = "translateY(-1px)";
                    e.currentTarget.style.boxShadow  = "0 4px 14px rgba(26,107,60,0.28)";
                  }
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = "#1a6b3c";
                  e.currentTarget.style.transform  = "none";
                  e.currentTarget.style.boxShadow  = "none";
                }}
                onClick={handleSubmit} disabled={btnLoading}>
                {btnLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    {type === "add" ? "Adding…" : "Updating…"}
                  </>
                ) : (
                  <>{type === "add" ? "✓ Add Presentation" : "✓ Update Presentation"}</>
                )}
              </button>
            </div>

          </div>
        )}
        <Footer />
      </div>
    </div>
  );
}

export default PresentationSetupForm;